import React, { useMemo, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";

import moment from "moment";

import TextInput from "@cayuse/shared-react.components.form.inputs.text";
import Toggle from "@cayuse/shared-react.components.form.inputs.toggle";
import Icon from "@cayuse/shared-react.components.icon";
import IconButton from "@cayuse/shared-react.components.icon-button";
import { crunchClasses } from "@cayuse/shared-react.utils.helpers";

import styles from "components/BudgetBuilder/BudgetSettings/CostCategoriesList/styles.css";
import Table from "components/Table";
import { STATIC_BUDGET_CATEGORIES } from "globals/constants";
import { useDictionary } from "hooks";
import { isPresent } from "utils/budgetBuilder";

const CostCategoriesRow = (
	{
		category,
		parentPosition,
		childPosition,
		isEditing,
		handleActiveStatusChange,
		handleChange,
		handleRemoveItem,
		budgetBuilderLastCreatedOn,
		moveItem,
	}
) => {
	const dictionary = useDictionary("categories");
	const { hash, code, value, active, createdOn } = category;
	const classNames = crunchClasses({
		[styles.row]: true,
		[styles.parentRow]: childPosition === undefined,
	});

	const isStatic = useMemo(
		() => STATIC_BUDGET_CATEGORIES.includes(category.value),
		[ category.value ]
	);

	const canBeDeactivated = !isStatic || category.value === "Subawards";

	const ref = useRef();

	const [ , drag ] = useDrag({
		canDrag: !isStatic,
		item: {
			id: hash,
			childPosition,
			parentPosition,
			type: isPresent(childPosition) ? `${parentPosition}child` : "parent",
		},
	});

	const [ , drop ] = useDrop({
		accept: isPresent(childPosition) ? `${parentPosition}child` : "parent",
		canDrop: () => !isStatic,
		drop: item => moveItem(item.parentPosition, item.childPosition, parentPosition, childPosition),
	});

	drag(drop(ref));

	const used = useMemo(() => {
		if (createdOn && moment.isMoment(budgetBuilderLastCreatedOn)) {
			return moment(new Date(createdOn)).isBefore(budgetBuilderLastCreatedOn);
		}

		return false;
	}, [ createdOn, budgetBuilderLastCreatedOn ]);

	return isEditing ? (
		<tr key={hash} id={hash} className={classNames} ref={ref}>
			<Table.Cell>
				<div>
					<div className={styles.dragIcon}><Icon name="grip-vertical" /></div>
					{isStatic ? (
						<p>{value}</p>
					) : (
						<TextInput
							disabled={isStatic}
							className={styles.input}
							value={value}
							onChange={event => handleChange(hash, { value: event.target.value })}
						/>
					)}

				</div>
			</Table.Cell>
			<Table.Cell>
				<TextInput
					value={code}
					onChange={event => handleChange(hash, { code: event.target.value })}
				/>
			</Table.Cell>
			<Table.Cell>
				<Toggle
					disabled={!canBeDeactivated}
					active={active}
					dictionary={dictionary.components.toggle}
					onClick={() =>  handleActiveStatusChange(hash, !category.active)}
				/>
			</Table.Cell>
			<Table.Cell>
				{!used && !isStatic && (
					<IconButton
						tertiary
						onClick={() => handleRemoveItem(parentPosition, childPosition)}
						className={styles.deleteButton}
						icon="trash"
					/>
				)}
			</Table.Cell>
		</tr>
	) : (
		<Table.Row key={hash} className={classNames}>
			<Table.Cell>{value}</Table.Cell>
			<Table.Cell>{code}</Table.Cell>
			<Table.Cell>
				{active ? dictionary.components.toggle.activeText : dictionary.components.toggle.inactiveText}
			</Table.Cell>
		</Table.Row>
	);
};

export default CostCategoriesRow;
